import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { type RootState } from '@/store';
import { MedicalMatchResponse } from '@/api';

/**
 * What the user had going in the panel — the search, a half-typed question and
 * how far down they'd scrolled. The panel unmounts on every sidebar tab switch,
 * so this is what puts it back as they left it. Tied to one case: opening
 * another starts the panel afresh rather than carrying a draft across.
 */
interface MedicalMatchPanelState {
  caseId: string | null;
  searchTerm: string;
  draftQuestion: string;
  scrollTop: number;
}

const initialPanelState: MedicalMatchPanelState = {
  caseId: null,
  searchTerm: '',
  draftQuestion: '',
  scrollTop: 0,
};

interface MedicalMatchState {
  fetchedCaseId: string | null;
  data: MedicalMatchResponse;
  isLoading: boolean;
  error: string | null;
  selectedEntryIndex: number | null;
  highlightPageId: string | null;
  highlightTexts: string[];
  isMedicalMatchTabActive: boolean;
  // Session-only "visited citation chips" — stored as an array (not a Set)
  // so it stays serialisable. Kept here rather than in local component state
  // so it survives the panel unmounting when the user switches sidebar tabs.
  visitedCitationPageIds: string[];
  panel: MedicalMatchPanelState;
}

const initialState: MedicalMatchState = {
  fetchedCaseId: null,
  data: [],
  isLoading: false,
  error: null,
  selectedEntryIndex: null,
  highlightPageId: null,
  highlightTexts: [],
  isMedicalMatchTabActive: false,
  visitedCitationPageIds: [],
  panel: initialPanelState,
};

const medicalMatchSlice = createSlice({
  name: 'medicalMatch',
  initialState,
  reducers: {
    setMedicalMatchData: (state, action: PayloadAction<{ caseId: string; data: MedicalMatchResponse }>) => {
      state.fetchedCaseId = action.payload.caseId;
      state.data = action.payload.data;
      state.error = null;
    },
    setMedicalMatchLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setMedicalMatchError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setMedicalMatchSelectedEntryIndex: (state, action: PayloadAction<number | null>) => {
      state.selectedEntryIndex = action.payload;
    },
    setMedicalMatchHighlight: (state, action: PayloadAction<{ pageId: string | null; texts: string[] }>) => {
      state.highlightPageId = action.payload.pageId;
      state.highlightTexts = action.payload.texts;
    },
    clearMedicalMatchHighlight: (state) => {
      state.highlightPageId = null;
      state.highlightTexts = [];
    },
    setMedicalMatchTabActive: (state, action: PayloadAction<boolean>) => {
      state.isMedicalMatchTabActive = action.payload;
    },
    addVisitedCitationPageId: (state, action: PayloadAction<string>) => {
      if (!state.visitedCitationPageIds.includes(action.payload)) {
        state.visitedCitationPageIds.push(action.payload);
      }
    },
    updateMedicalMatchPanel: (
      state,
      action: PayloadAction<{ caseId: string } & Partial<Omit<MedicalMatchPanelState, 'caseId'>>>,
    ) => {
      const { caseId, ...changes } = action.payload;
      if (state.panel.caseId !== caseId) state.panel = { ...initialPanelState, caseId };
      Object.assign(state.panel, changes);
    },
    resetMedicalMatch: () => initialState,
  },
});

export const {
  setMedicalMatchData,
  setMedicalMatchLoading,
  setMedicalMatchError,
  setMedicalMatchSelectedEntryIndex,
  setMedicalMatchHighlight,
  clearMedicalMatchHighlight,
  setMedicalMatchTabActive,
  addVisitedCitationPageId,
  updateMedicalMatchPanel,
  resetMedicalMatch,
} = medicalMatchSlice.actions;

// Selectors
export const selectMedicalMatchData = (state: RootState) => state.medicalMatch.data;

export const selectMedicalMatchFetchedCaseId = (state: RootState) => state.medicalMatch.fetchedCaseId;

export const selectMedicalMatchIsLoading = (state: RootState) => state.medicalMatch.isLoading;

export const selectMedicalMatchError = (state: RootState) => state.medicalMatch.error;

export const selectMedicalMatchSelectedEntryIndex = (state: RootState) => state.medicalMatch.selectedEntryIndex;

export const selectMedicalMatchHighlightPageId = (state: RootState) => state.medicalMatch.highlightPageId;

export const selectMedicalMatchHighlightTexts = (state: RootState) => state.medicalMatch.highlightTexts;

export const selectMedicalMatchTabActive = (state: RootState) => state.medicalMatch.isMedicalMatchTabActive;

export const selectMedicalMatchVisitedCitationPageIds = (state: RootState) => state.medicalMatch.visitedCitationPageIds;

// The panel's saved state reads as empty for any case other than the one it was
// saved against, so a panel opening on a new case never picks up the last one's.
const selectMedicalMatchPanelFor = (state: RootState, caseId: string | undefined) =>
  state.medicalMatch.panel.caseId === caseId ? state.medicalMatch.panel : initialPanelState;

export const selectMedicalMatchPanelSearchTerm = (state: RootState, caseId: string | undefined) =>
  selectMedicalMatchPanelFor(state, caseId).searchTerm;

export const selectMedicalMatchPanelDraftQuestion = (state: RootState, caseId: string | undefined) =>
  selectMedicalMatchPanelFor(state, caseId).draftQuestion;

export const selectMedicalMatchPanelScrollTop = (state: RootState, caseId: string | undefined) =>
  selectMedicalMatchPanelFor(state, caseId).scrollTop;

export const medicalMatchReducer = medicalMatchSlice.reducer;
